import axios from 'axios';
import { toast } from 'react-hot-toast';

// Determine API URL based on environment
const isProduction = process.env.NODE_ENV === 'production';
const API_URL = isProduction 
  ? (process.env.REACT_APP_PRODUCTION_API_URL || 'https://api.production.com/api')
  : (process.env.REACT_APP_API_URL || 'http://localhost:5000/api');

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 10000, // 10 seconds timeout
});

// Request interceptor to add token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error) => {
    const { response } = error;
    
    if (response) {
      // Handle specific HTTP status codes
      switch (response.status) {
        case 401:
          // Unauthorized - clear localStorage and redirect to login
          localStorage.removeItem('token');
          localStorage.removeItem('userRole');
          localStorage.removeItem('user');
          window.location.href = '/login';
          toast.error('Session expired. Please login again.');
          break;
        
        case 403:
          // Forbidden
          toast.error('You do not have permission to perform this action.');
          break;
        
        case 404:
          // Not Found
          toast.error('Resource not found.');
          break;
        
        case 422:
          // Validation Error
          const validationErrors = response.data?.errors;
          if (validationErrors) {
            validationErrors.forEach(err => {
              toast.error(err.msg || 'Validation error');
            });
          } else {
            toast.error(response.data?.message || 'Validation error');
          }
          break;
        
        case 500:
          // Server Error
          toast.error('Server error. Please try again later.');
          break;
        
        default:
          // Other errors
          toast.error(response.data?.message || 'An error occurred');
      }
    } else if (error.code === 'ECONNABORTED') {
      toast.error('Request timeout. Please try again.');
    } else if (error.message === 'Network Error') {
      toast.error('Network error. Please check your connection.');
    }
    
    return Promise.reject(error);
  }
);

// Auth API calls
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getProfile: () => api.get('/auth/me'),
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userRole');
    localStorage.removeItem('user');
  }
};

// Order API calls
export const orderAPI = {
  getAll: (params = {}) => api.get('/orders', { params }),
  getById: (id) => api.get(`/orders/${id}`),
  create: (orderData) => api.post('/orders', orderData),
  update: (id, orderData) => api.put(`/orders/${id}`, orderData),
  delete: (id) => api.delete(`/orders/${id}`)
};

// Production API calls
export const productionAPI = {
  record: (productionData) => api.post('/production', productionData),
  getDaily: (date) => api.get(`/production/daily/${date}`),
  getByOrder: (orderId) => api.get(`/production/order/${orderId}`)
};

// Expense API calls
export const expenseAPI = {
  getAll: (params = {}) => api.get('/expenses', { params }),
  create: (expenseData) => api.post('/expenses', expenseData),
  update: (id, expenseData) => api.put(`/expenses/${id}`, expenseData),
  delete: (id) => api.delete(`/expenses/${id}`)
};

// Report API calls
export const reportAPI = {
  getMonthly: (month, year) => api.get('/reports/monthly', { params: { month, year } }),
  getDashboard: () => api.get('/reports/dashboard')
};

// Billing API calls
export const billingAPI = {
  getAll: (params = {}) => api.get('/billings', { params }),
  getById: (id) => api.get(`/billings/${id}`),
  create: (data) => api.post('/billings', data),
  update: (id, data) => api.put(`/billings/${id}`, data),
  delete: (id) => api.delete(`/billings/${id}`)
};

// User API calls (admin only)
export const userAPI = {
  getAll: () => api.get('/users'),
  getById: (id) => api.get(`/users/${id}`),
  update: (id, userData) => api.put(`/users/${id}`, userData),
  delete: (id) => api.delete(`/users/${id}`)
};

// Export the axios instance for custom requests
export default api;
